import { useEffect, useState } from 'react';
import { CuttingModal2 } from './CuttingModal2';
import type { CuttingDocument } from '../model/types';
import type { OptimizationResult } from '@pomoshnik/shared';
import { renderCuttingPdf } from '../lib/cutPdf';
import { buildPartsCsv } from '../lib/partsCsv';
import { buildPartsXlsxBlob } from '../lib/partsXlsx';
import { shareCuttingPdf } from '../lib/shareCutPdf';
import { useCuttingStore } from '../state/useCuttingStore';

/**
 * Block 14 «Раскрой» — модалка «Отправить на распил — выбор формата» (PR share-cut-pdf).
 *
 * Три формата (вертикальный список, P-019):
 *  - **PDF карты раскроя → ОТПРАВИТЬ** через Web Share API (системное окно
 *    «Поделиться» с файлом + текстом, по образцу КП). Web Share принимает
 *    `application/pdf`. Логика — `shareCuttingPdf` (cutting-local, паттерн КП).
 *  - **CSV / Excel → СКАЧАТЬ.** Web Share не принимает эти типы (`canShare=false`);
 *    ссылка-в-мессенджер для них — будущая фича (нужен R2 + ADR), здесь только download.
 *
 * Все три файла пред-собираются в `useEffect` ЗАРАНЕЕ (PDF тяжёлый: шрифт +
 * растеризация карты). Пока не готовы — кнопки disabled, статус «Формирую файлы…».
 * PDF передаётся в `shareCuttingPdf` как `buildPdf: async () => pdfBlob` (резолв
 * мгновенный) → первый реальный await в обработчике клика = `navigator.share`,
 * transient-activation (popup gesture) не рвётся.
 *
 * Клиентское (P-009): ничего не уходит на сервер.
 */

/** UTF-8 BOM — Excel-RU читает CSV в UTF-8 (стандарт экспорта CSV в проекте). */
const BOM = '\uFEFF';

interface PreBuilt {
  pdfBlob: Blob;
  csvFile: File;
  xlsxFile: File;
}

function downloadFile(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ShareFormatDialog({
  doc,
  result,
  onClose,
}: {
  doc: CuttingDocument;
  result: OptimizationResult;
  onClose: () => void;
}) {
  const [files, setFiles] = useState<PreBuilt | null>(null);
  const [status, setStatus] = useState('Формирую файлы…'); // loading (P-021)
  // PDF следует экранным тогглам Кромки/Пазы (UI-only стор) — как печать.
  const mapShowEdges = useCuttingStore((s) => s.mapShowEdges);
  const mapShowGrooves = useCuttingStore((s) => s.mapShowGrooves);

  // Пред-сборка всех трёх форматов заранее. Гонка/размонтирование — флаг alive.
  useEffect(() => {
    let alive = true;
    setFiles(null);
    setStatus('Формирую файлы…');
    void (async () => {
      try {
        const date = new Date().toISOString().slice(0, 10);
        const pdfBlob = await renderCuttingPdf(doc, result, {
          showEdges: mapShowEdges,
          showGrooves: mapShowGrooves,
        });
        const csv = buildPartsCsv(doc);
        const csvFile = new File([BOM + csv], `Список_деталей_${date}.csv`, {
          type: 'text/csv;charset=utf-8',
        });
        const xlsxBlob = await buildPartsXlsxBlob(doc);
        const xlsxFile = new File([xlsxBlob], `Список_деталей_${date}.xlsx`, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        if (!alive) return;
        setFiles({ pdfBlob, csvFile, xlsxFile });
        setStatus('');
      } catch {
        if (!alive) return;
        setStatus('Не удалось сформировать файлы. Попробуйте ещё раз.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [doc, result, mapShowEdges, mapShowGrooves]);

  // Обработчик PDF — синхронный (без await до shareCuttingPdf): первый await внутри
  // shareCuttingPdf — copyClipboard (быстро) + navigator.share, transient-activation
  // не рвётся (как в КП). pdfBlob пред-собран → buildPdf резолвит мгновенно.
  const handleSharePdf = () => {
    if (!files) return;
    void shareCuttingPdf(doc, result, { buildPdf: async () => files.pdfBlob }).then((r) => {
      if (r === 'shared') setStatus('Отправлено');
      else if (r === 'downloaded') setStatus('PDF скачан — отправьте в мессенджере');
      else if (r === 'cancelled') setStatus('');
      else setStatus('Не удалось отправить PDF. Попробуйте ещё раз.');
    });
  };

  const ready = files !== null;

  return (
    <CuttingModal2
      title="Отправить на распил — выбор формата"
      footer={
        <button type="button" className="sc-modal-btn" onClick={onClose}>
          Закрыть
        </button>
      }
    >
      <div
        className="sc-share-format-list"
        style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}
      >
        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={handleSharePdf}
          disabled={!ready}
        >
          🗎 PDF — карты раскроя
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-lg"
          onClick={() => files && downloadFile(files.xlsxFile)}
          disabled={!ready}
        >
          📊 Excel — детали и кромки
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-lg"
          onClick={() => files && downloadFile(files.csvFile)}
          disabled={!ready}
        >
          📋 CSV — детали и кромки
        </button>
        <p className="sc-result-actions-status" role="status" aria-live="polite">
          {status}
        </p>
      </div>
    </CuttingModal2>
  );
}
